import React, { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { supabase } from '../services/supabaseClient';
// Model selection imports removed - defaulting to Sonnet

interface WorkspaceModalProps {
  orgId: string;
  workspace?: any;
  onClose: () => void;
}

const PROMPT_TEMPLATES = {
  'general': {
    name: 'General Documentation',
    description: 'Comprehensive flow documentation for business users',
    template: `Analyze this Salesforce Flow and provide a comprehensive analysis. Start immediately with **Purpose**. Don't include any preamble:

{flowStructure}

**Purpose:** Brief explanation of what this flow accomplishes from a business perspective.

**Flow Summary:** Comprehensive overview of the flow's logic, including when it triggers, the overall execution path, and how it achieves its purpose.

**Actions:** List the key actions performed by this flow in order of execution. Use bullet points or numbered list.

**Key Decision Points:** Describe any conditional logic, branching, or decision elements that affect the flow's execution path.

**Objects/Records Modified:** List all Salesforce objects that are created, read, updated, or deleted by this flow.

Keep the analysis clear and business-focused while being technically accurate.`
  },
  'pii-compliance': {
    name: 'PII Compliance Audit',
    description: 'Analyzes flows for personal data handling and privacy compliance',
    template: `Analyze this Salesforce Flow for PII (Personally Identifiable Information) handling:

1. **PII Fields Identified**: List all fields that contain personal data
2. **Data Collection**: How is personal data collected and from which sources?
3. **Data Storage**: Where is PII stored and for how long?
4. **Data Sharing**: Is PII shared with external systems or users?
5. **Access Controls**: Who can view or modify PII in this flow?
6. **Compliance Risks**: Any GDPR, CCPA, or privacy regulation concerns?
7. **Recommendations**: Suggestions for improving PII handling

Flag any potential privacy violations or security concerns.`
  },
  'performance': {
    name: 'Performance Analysis',
    description: 'Identifies bottlenecks and optimization opportunities',
    template: `Analyze this Salesforce Flow for performance optimization. Be concise.

FLOW CONTEXT:
- Trigger Object: {triggerObject}
- Records Updated: {recordsUpdated}
- Relationship: {relationship}

**1. QUERY ANALYSIS**
Count recordLookups. Note indexed fields, loops, per-path execution.

**2. DML OPERATIONS**  
List ALL recordCreates/Updates/Deletes by name. Count total vs per-path.

**PATH ANALYSIS RULES**:
- Trace each path from start to end
- When decisions branch, only ONE branch executes
- Count operations along each complete path
- Common pattern: Null-check decisions create mutually exclusive updates
  Example: If null → set to 1, If not null → increment
  This = 1 DML per path, not 2

**3. ARCHITECTURE**
- Before-Save possible? (ONLY if updating trigger record)
- Fast Updates possible? (ONLY if updating trigger record)
- Better alternatives? (Roll-up summary, formula, etc.)

**4. FORMULAS & DECISIONS**
Identify BLANKVALUE() opportunities and redundant logic.
Note: Decisions checking different objects/fields cannot be combined.

**5. GOVERNOR LIMITS**
- Single: X/100 SOQL, Y/150 DML per path
- Bulk (200): Calculate based on per-path counts
- Remember: Only one path executes per record

**6. TOP 3 OPTIMIZATIONS**
Specific, actionable improvements ranked by impact.

CONSTRAINTS:
- No UNION in SOQL
- Before-Save can't update related records  
- Fast Updates only for trigger record
- Count per-path, not total elements
- Mutually exclusive branches = 1 operation, not multiple`
  },
  'security': {
    name: 'Security Audit',
    description: 'Reviews flows for security vulnerabilities and access control',
    template: `Conduct a security audit of this Salesforce Flow:

**Access Controls**: How are user permissions enforced?
**Data Exposure**: What sensitive data is accessed or exposed?
**External Integrations**: Review security of external callouts
**Input Validation**: Check for proper data validation
**Error Messages**: Ensure errors don't leak sensitive information
**CRUD/FLS**: Verify field and object-level security is respected
**Vulnerabilities**: Identify potential security risks

Rate security concerns as Critical, High, Medium, or Low.`
  }
};

export default function WorkspaceModal({ orgId, workspace, onClose }: WorkspaceModalProps) {
  const { createWorkspace, updateWorkspace, testPrompt } = useWorkspace();
  // Removed - we'll fetch flows directly for this org
  const [name, setName] = useState(workspace?.name || '');
  const [description, setDescription] = useState(workspace?.description || '');
  const [promptTemplate, setPromptTemplate] = useState(workspace?.prompt_template || '');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [flows, setFlows] = useState<any[]>([]);
  const [flowsLoading, setFlowsLoading] = useState(false);
  
  // Model selection removed - always use Sonnet

  const isEdit = !!workspace;

  // Fetch flows for the org to allow testing
  useEffect(() => {
    const fetchFlows = async () => {
      if (!orgId) return;
      
      setFlowsLoading(true);
      try {
        const { data, error } = await supabase
          .from('sf_flow_definitions')
          .select('id, label')
          .eq('org_id', orgId)
          .eq('status', 'Active')
          .order('label');
        
        if (error) throw error;
        setFlows(data || []);
      } catch (err) {
        console.error('Error fetching flows:', err);
        setFlows([]);
      } finally {
        setFlowsLoading(false);
      }
    };

    fetchFlows();
  }, [orgId]);

  useEffect(() => {
    if (!isEdit && selectedTemplate && PROMPT_TEMPLATES[selectedTemplate as keyof typeof PROMPT_TEMPLATES]) {
      const template = PROMPT_TEMPLATES[selectedTemplate as keyof typeof PROMPT_TEMPLATES];
      setName(template.name);
      setDescription(template.description);
      setPromptTemplate(template.template);
    }
  }, [selectedTemplate, isEdit]);

  // Model analysis removed - always use Sonnet

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (isEdit) {
        await updateWorkspace(workspace.id, {
          name,
          description,
          prompt_template: promptTemplate,
          claude_model: 'sonnet',
          model_selection_type: 'auto',
          model_rationale: 'Default model'
        });
      } else {
        await createWorkspace({
          org_id: orgId,
          name,
          description,
          prompt_template: promptTemplate,
          claude_model: 'sonnet',
          model_selection_type: 'auto',
          model_rationale: 'Default model'
        });
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save workspace');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTestPrompt = async () => {
    if (!promptTemplate || flows.length === 0) return;

    setIsTesting(true);
    setTestResult(null);
    
    try {
      // Test on the first active flow
      const testFlow = flows.find(f => f.status === 'Active') || flows[0];
      const result = await testPrompt(workspace?.id || 'test', testFlow.id);
      setTestResult(result);
    } catch (err: any) {
      setError(err.message || 'Failed to test prompt');
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {isEdit ? 'Edit Workspace' : 'Create New Workspace'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start with a template (optional)
              </label>
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="block w-full pl-3 pr-10 py-3 text-base border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-md"
              >
                <option value="">Custom prompt</option>
                <option value="general">General Documentation</option>
                <option value="pii-compliance">PII Compliance Audit</option>
                <option value="performance">Performance Analysis</option>
                <option value="security">Security Audit</option>
              </select>
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Workspace Name
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., PCI Compliance Analysis"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description (optional)
            </label>
            <input
              type="text"
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1 block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Brief description of this workspace's purpose"
            />
          </div>

          <div>
            <label htmlFor="prompt" className="block text-sm font-medium text-gray-700">
              AI Prompt Template
            </label>
            <textarea
              id="prompt"
              value={promptTemplate}
              onChange={(e) => setPromptTemplate(e.target.value)}
              required
              rows={10}
              className="mt-1 block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter the prompt that will be used to analyze flows in this workspace..."
            />
            <p className="mt-2 text-sm text-gray-500">
              This prompt will be used to generate AI summaries for all flows when the workspace is activated.
            </p>
          </div>


          {flows.length > 0 && promptTemplate && (
            <div>
              <button
                type="button"
                onClick={handleTestPrompt}
                disabled={isTesting}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {isTesting ? 'Testing...' : 'Test Prompt on Sample Flow'}
              </button>
              
              {testResult && (
                <div className="mt-4 p-4 bg-gray-50 rounded-md">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Test Result for: {testResult.flow_label}
                  </h4>
                  <div className="text-sm text-gray-600 whitespace-pre-wrap">
                    {testResult.sample_summary}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Tokens used: {testResult.tokens_used}
                  </p>
                </div>
              )}
            </div>
          )}
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !name || !promptTemplate}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Workspace'}
          </button>
        </div>
      </div>
    </div>
  );
}